import { useContext } from 'react';
import { Box } from '@mui/material';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import EmailBounceStateIndicator from '../Mail/EmailBounceStateIndicator';

function CustomerManagement() {
  const authCtx = useContext(AuthContext);

  if (!authCtx.checkRight('customer:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const canUseNonStandardPostcode = authCtx.checkRight('internalBooking:special:nonStandardPostcode');

  const customerSchema = {
    companyName: {
      label: 'Firma',
      type: 'text',
      required: false,
      minWidth: '150px',
    },
    email: {
      label: 'E-Mail',
      type: 'email',
      required: true,
      minWidth: '220px',
      listDisplayValue: (value, item) => (
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <span>{value || '—'}</span>
          <EmailBounceStateIndicator state={item?.emailBounceState} showSoftBounces />
        </Box>
      ),
    },
    salutation: {
      label: 'Anrede',
      type: 'select',
      placeholder: 'Anrede auswählen',
      options: [
        { label: 'Keine Anrede', value: '' },
        { label: 'Herr', value: 'Herr' },
        { label: 'Frau', value: 'Frau' },
        { label: 'Dr.', value: 'Dr.' },
      ],
      required: false,
      minWidth: '80px',
    },
    firstName: {
      label: 'Vorname',
      type: 'text',
      required: false,
      minWidth: '120px',
    },
    lastName: {
      label: 'Nachname',
      type: 'text',
      required: false,
      minWidth: '120px',
    },
    street: {
      label: 'Straße',
      type: 'text',
      required: false,
      minWidth: '150px',
    },
    houseNumber: {
      label: 'Hausnummer',
      type: 'text',
      required: false,
      minWidth: '40px',
    },
    postalCode: {
      label: 'PLZ',
      type: 'zipcode',
      required: false,
      minWidth: '80px',
      allowNonStandard: canUseNonStandardPostcode,
    },
    city: {
      label: 'Ort',
      type: 'text',
      required: false,
      minWidth: '120px',
    },
    addressAddition: {
      label: 'Adresszusatz',
      type: 'text',
      required: false,
      minWidth: '120px',
    },
    jobCode: {
      label: 'Jobcode',
      type: 'text',
      required: false,
      minWidth: '100px',
    },
    contactPerson: {
      label: 'Ansprechpartner',
      type: 'text',
      required: false,
      minWidth: '150px',
    },
    phone: {
      label: 'Telefonnummer',
      type: 'phone',
      required: false,
      minWidth: '120px',
    },
    customerNumber: {
      label: 'Kundennummer',
      type: 'number',
      required: false,
      excludeFromAdd: true,
      readOnly: true,
      minWidth: '100px',
    },
    canBill: {
      label: 'Rechnungskunde',
      type: 'boolean',
      required: false,
    },
    active: {
      label: 'Aktiv',
      type: 'boolean',
      required: false,
      excludeFromAdd: true,
    },
    validUntil: {
      label: 'Gültig bis',
      type: 'date',
      required: false,
      excludeFromAdd: true,
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={customerSchema}
      title='Neuen Kunden hinzufügen'
      resourceType='customer'
      searchPlaceholder='Kunden durchsuchen'
      parentIsEditable={authCtx.checkRight('customer:update')}
      visibleColumnKeys={[
        'companyName',
        'salutation',
        'lastName',
        'firstName',
        'phone',
        'email',
        'customerNumber',
        'canBill',
        'active',
        'validUntil',
      ]}
    />
    </Box>
  );
}

export default CustomerManagement;
